/**
 * One open price list (T387 M5): place + registry band columns, and the ONE
 * row grammar everywhere — the PRICE is the tap target (context lives in the
 * accessible name, which is what lets all 4 band columns + the place name fit
 * 1024x768; the shipped screen's visible "Edit <label> <band>" text was the
 * width culprit), inline edit strip with a required reason, gold provisional
 * rows with a provenance line, one-note ratify, per-cell history.
 *
 * Venue-kind lists (weddings, tours) also show KNOWN-but-unpriced venues as
 * honest empty rows ("No set price. Quoted by hand each time.") — never an
 * empty cell pretending to be a price of zero.
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import { PRICE_ENTRY_KIND_BY_SERVICE, type ServiceId } from '../../../shared/service-schema.js';
import { closureNoteFor } from '../../../shared/course-closures.js';
import { slugify } from '../../../shared/pricing-flatten.js';
import { formatRouteLabel } from '../../../shared/route-label.js';
import {
  saveRate,
  setRateCell,
  ratifyRate,
  listPriceAudit,
  type ApiRateRow,
  type PriceEntryKind,
} from '../../lib/actions.js';
import { getOr } from '../../lib/api.js';
import { q36MagnitudeSlip } from '../../lib/q36.js';
import { bands, type Registry } from '../../store/registry.js';
import { euro, shortDate } from '../../lib/format.js';
import { Banner } from '../../components/Banner.js';
import { COVER_NAMES, LIST_NOUNS } from './Shelf.js';
import { AddEntryForm, ADD_LABELS } from './AddEntryForm.js';
import { AuditHistory } from './AuditHistory.js';

export type RateListProps = {
  registry: Registry;
  service: string;
  rates: ApiRateRow[];
  initialFilter?: string;
  onBack: () => void;
  /** Re-load rates after any write. */
  onChanged: () => void;
};

/** One service+location row, its rates keyed by band. */
type RateGroup = {
  locationId: string;
  label: string;
  cells: Map<string, ApiRateRow>;
  provisional: boolean;
};

type Provenance = { actor: string; at: string; sourceBookingId: number | null };

/**
 * ⛔ THE REASON IS ABOUT A FIGURE, NOT ONLY ABOUT A ROW (board 717 (a)).
 *
 * A `rate_audit` row records `old -> new, BECAUSE <note>`, so the AMOUNT is part of what the reason
 * is about. Both strips below pair a shared note field with a TYPED money figure and neither reset
 * the note when the amount moves, so a sentence typed about one figure could ride a different one
 * into the journal. Ported from `PriceList.tsx` (f0bee95), with two deliberate differences recorded
 * at the guard itself.
 *
 * ⚠️ NOT the precedent's sentence, on purpose: `PriceList`'s begins "The price changed since you last
 * tried" and `price-list.spec.tsx` asserts exactly that prefix, so a shared opening would let two
 * screens' pins satisfy each other by accident. "Amount" is this screen's own word for the field
 * (see the Q36 copy below).
 */
const FIGURE_MOVED_NOTICE = 'The amount changed since you last tried. Say why again.';

export function RateList({ registry, service, rates, initialFilter, onBack, onChanged }: RateListProps) {
  const [filter, setFilter] = useState(initialFilter ?? '');
  const [editing, setEditing] = useState<string | null>(null);
  const [editEuros, setEditEuros] = useState('');
  const [editNote, setEditNote] = useState('');
  const [ratifying, setRatifying] = useState<string | null>(null);
  const [ratifyNote, setRatifyNote] = useState('');
  const [historyFor, setHistoryFor] = useState<string | null>(null);
  const [amountWarned, setAmountWarned] = useState(false);
  // `atStrip` routes a rejection INSIDE the open strip (beside the field it
  // names) instead of the page-top banner — a row below the fold got no visible
  // feedback otherwise (FB1). FIVE rejections set it: the three reason-required
  // ones, plus the TWO figure-moved ones added for board 717 (a). Price-invalid /
  // magnitude-warn / server errors + success stay at top (success fires after the
  // strip has closed).
  // ⚠️ `reasonErrorAtStrip` therefore now carries a rejection that is not, strictly,
  // "reason required" — a figure-moved refusal. Routing it here IS correct: it
  // clears the note and she does have to retype one, so the field it points at is
  // the right field and `aria-invalid` on it is true. The identifier is kept rather
  // than renamed across its five call sites; this comment is the correction.
  const [notice, setNotice] = useState<{ kind: 'ok' | 'error'; text: string; atStrip?: boolean } | null>(null);
  // T399 item 2 — the empty-cell "Set price" strip: (place name, band) being
  // set. Separate from `editing` (which edits an existing row) — this creates a
  // single new provisional cell. Only one strip is ever open at a time.
  const [setting, setSetting] = useState<{ name: string; label: string; band: string } | null>(null);
  const [settingEuros, setSettingEuros] = useState('');
  const [settingNote, setSettingNote] = useState('');
  const [settingWarned, setSettingWarned] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [knownVenues, setKnownVenues] = useState<string[]>([]);
  const [provenance, setProvenance] = useState<Record<string, Provenance>>({});
  /**
   * The CENTS the strip showed at her last SAVE PRESS, or null if she has not pressed Save since the
   * strip opened. See FIGURE_MOVED_NOTICE. Refs, not state: read and written synchronously inside
   * one submit, and a re-render would be pointless work.
   *
   * ⛔ "SAVE PRESS", NOT "ATTEMPT THAT REACHED THE SERVER" - the distinction is the whole guard.
   * Every path that returns after computing the figure records it: the send, the Q36 warn, AND the
   * blank-note refusal. An earlier draft armed only the first two and was bypassable by the
   * commonest sequence there is (Save blank, type the reason, then change the amount).
   *
   * ⛔⛔ WHAT THIS DOES **NOT** COVER, STATED PLAINLY BECAUSE THE FIRST DRAFT OVERCLAIMED IT.
   * The ref starts null, so THE FIRST SAVE PRESS OF A STRIP IS UNGUARDED BY CONSTRUCTION. Type a
   * reason about 125, change the amount to 130, press Save once: it sends, and the journal reads
   * `120 -> 130, because <a sentence about 125>`. MEASURED, not theorised (gating security-review,
   * second pass). The precedent has the identical limit.
   * ⇒ THE PROPERTY THIS GUARD ACTUALLY DELIVERS IS: **a reason cannot survive a figure change
   * ACROSS A SAVE PRESS.** It binds RETRIES - the sequences where she has already been told
   * something and come back - not first composition.
   * ⚠️ AND IT CANNOT BE EXTENDED TO COVER THAT without making the screen worse. The binding event
   * would have to be "the figure on screen when she typed the sentence", and within one
   * uninterrupted composition that information does not exist: arming on the note's `onInput` would
   * REFUSE the legitimate "write the reason, then set the amount" flow, because this strip pre-fills
   * the OLD price. Clearing the note on every euro keystroke is the other option and is the one this
   * codebase deliberately rejected - it eats her sentence for a slip she immediately corrects.
   * So this is a recorded limit, not a TODO. Do not "close" it without re-arguing that trade.
   *
   * ⛔ CENTS, NOT THE TYPED STRING — the one place this deliberately improves on the precedent.
   * `PriceList` compares `amount.trim()`, so `125` -> `125.00` reads as a changed figure and eats her
   * sentence for a keystroke that changes nothing. What reaches `rate_audit` is the cents, and equal
   * cents means a byte-identical journal row, so the reason is equally true. Junk never gets here:
   * '', ' ', 'abc', '1e400', negatives and zero all return at the euro-validity gate above.
   */
  const editNoteFigure = useRef<number | null>(null);
  const settingNoteFigure = useRef<number | null>(null);

  const kind = PRICE_ENTRY_KIND_BY_SERVICE[service as ServiceId] ?? null;
  const bandCols = bands(registry);
  const rows = rates.filter((r) => r.service === service);

  // Venue-kind lists show known-but-unpriced venues as honest empty rows.
  useEffect(() => {
    if (kind !== 'venue') return;
    if (service === 'wedding') {
      void getOr<{ venues: string[] }>('/api/venues', { venues: [] }).then((d) => setKnownVenues(d.venues));
    } else {
      void getOr<{ suggestions: string[] }>(
        `/api/venue-suggestions?service=${encodeURIComponent(service)}`,
        { suggestions: [] },
      ).then((d) => setKnownVenues(d.suggestions));
    }
  }, [service, kind]);

  // Group rows per location (server sort preserved by first-seen insertion).
  const groups: RateGroup[] = [];
  {
    const byKey = new Map<string, RateGroup>();
    for (const r of rows) {
      let g = byKey.get(r.locationId);
      if (!g) {
        g = { locationId: r.locationId, label: r.label, cells: new Map(), provisional: false };
        byKey.set(r.locationId, g);
        groups.push(g);
      }
      g.cells.set(r.band, r);
      if (r.operatorAdded && !r.ratified) g.provisional = true;
    }
  }
  // Honest empties: known venues with no rates row yet.
  const pricedLabels = new Set(groups.map((g) => g.label.toLowerCase()));
  const emptyRows: string[] =
    kind === 'venue' ? knownVenues.filter((v) => !pricedLabels.has(v.trim().toLowerCase())) : [];

  // Lazy provenance for provisional rows: the oldest audit entry on the first
  // provisional cell is the insert event (who added it, when, from which booking).
  useEffect(() => {
    for (const g of groups) {
      if (!g.provisional || provenance[g.locationId]) continue;
      const cell = [...g.cells.values()].find((r) => r.operatorAdded && !r.ratified);
      if (!cell) continue;
      void listPriceAudit({ service, locationId: g.locationId, band: cell.band })
        .then((entries) => {
          const oldest = entries[entries.length - 1];
          if (!oldest) return;
          setProvenance((p) => ({
            ...p,
            [g.locationId]: { actor: oldest.actor, at: oldest.at, sourceBookingId: oldest.sourceBookingId },
          }));
        })
        .catch(() => { /* provenance is presentation only — degrade silently */ });
    }
    // groups is derived from props; keying on the ids string keeps this stable.
  }, [groups.map((g) => g.locationId).join(','), service]);

  const f = filter.trim().toLowerCase();
  // Match the STORED label or the DISPLAYED one: a golf row shows a single
  // arrow but is stored with the em-dash pair, so filtering on what Helen can
  // actually see off the screen must not come back empty (T494).
  //
  // T660 M6a — the second term MUST track whatever the ROWS actually render, and
  // this is the one MATCHING site in this file (every other call below is a
  // render). M6 moved all five together to `displayRouteLabel`; M6a moved all
  // five back, because `g.label` is `rates.location_label`, which was raw
  // operator input, so the wider collapse rewrote a venue or course name Helen
  // typed. The writer now refuses a typed boundary (server/app.ts
  // parseRateEntryBody, core/data/audit-repo.ts insertGolfRoute) and the RENDER
  // stays narrow: rows already stored before that refusal keep their own bytes.
  const shown = f
    ? groups.filter((g) => `${g.label}\n${formatRouteLabel(g.label)}`.toLowerCase().includes(f))
    : groups;
  const shownEmpty = f ? emptyRows.filter((v) => v.toLowerCase().includes(f)) : emptyRows;

  const cellKey = (r: ApiRateRow) => `${r.locationId}|${r.band}`;

  const startEdit = (r: ApiRateRow) => {
    setRatifying(null);
    setHistoryFor(null);
    setSetting(null);
    setEditing(cellKey(r));
    setEditEuros(r.priceCents != null ? String(r.priceCents / 100) : '');
    setEditNote('');
    editNoteFigure.current = null; // a new target: no reason is owed to any figure yet
    setAmountWarned(false); // Q36 re-arms per strip
    setNotice(null);
  };

  // T399 item 2: "Set price" is offered on empty cells of the town/venue lists
  // (a route cell needs its hub — out of scope; local-other has no list).
  const canSetPrice = kind === 'town' || kind === 'venue';

  const startSet = (name: string, label: string, band: string) => {
    setEditing(null);
    setRatifying(null);
    setHistoryFor(null);
    setSetting({ name, label, band });
    setSettingEuros('');
    setSettingNote('');
    settingNoteFigure.current = null; // a new target: see startEdit
    setSettingWarned(false);
    setNotice(null);
  };

  const commitSet = async () => {
    if (setting == null || kind == null) return;
    const euros = Number(settingEuros);
    if (!Number.isFinite(euros) || euros <= 0) {
      setNotice({ kind: 'error', text: 'Enter a price in euro before saving.' });
      return;
    }
    // Hoisted above the reason gates so the figure guard can compare it. Pure, and `euros` is
    // already validated by the gate above, so this is behaviour-neutral.
    const newCents = Math.round(euros * 100);
    // ⛔ THE FIGURE MOVED — see FIGURE_MOVED_NOTICE. Checked BEFORE the blank-note gate: this
    // refusal is the one that ADVANCES the ref, so taking it first costs her one retype instead of
    // two when she changes the amount twice before typing a reason.
    if (settingNoteFigure.current !== null && newCents !== settingNoteFigure.current) {
      // ⛔⛔ THE ADVANCE AND THE CLEAR BELOW ARE ONE MECHANISM. The advance stops the strip
      // LIVELOCKING (the precedent freezes its ref here, so every later save with the new figure
      // refuses again and eats the sentence she just typed — measured on PriceList, board pending).
      // But with the ref advanced, the CLEAR is the whole guarantee: leave the note standing and she
      // presses Save again unchanged, the figure now matches, and the stale reason posts against it.
      settingNoteFigure.current = newCents;
      setSettingNote('');
      setNotice({ kind: 'error', text: FIGURE_MOVED_NOTICE, atStrip: true });
      return;
    }
    if (!settingNote.trim()) {
      // ⛔ ARM HERE TOO - see commitEdit's blank-note gate for the sequence this closes.
      settingNoteFigure.current = newCents;
      setNotice({ kind: 'error', text: 'A reason note is required when adding a price.', atStrip: true });
      return;
    }
    // Q36 new-entry ceiling (no existing price to compare against) — warn once.
    if (!settingWarned && q36MagnitudeSlip(newCents, null)) {
      setSettingWarned(true);
      // ⛔ ARM HERE TOO, not only before the send. This branch returns WITHOUT sending, so an
      // unarmed ref leaks on the FIRST save of a fresh strip with no server error at all: type a
      // slipped figure and a reason naming it, get warned, correct the figure, save, and the reason
      // rides the corrected amount into the journal. The precedent cannot afford this arm because
      // its frozen ref would livelock; the advance above makes it cost exactly one retype.
      settingNoteFigure.current = newCents;
      setNotice({ kind: 'error', text: 'That looks very high for a standing price. Check the amount, then save again to confirm.' });
      return;
    }
    // What this attempt is about, so a later change of figure invalidates the reason.
    settingNoteFigure.current = newCents;
    const res = await setRateCell({
      kind: kind as PriceEntryKind,
      service,
      name: setting.name,
      band: setting.band,
      cents: newCents,
      note: settingNote.trim(),
    });
    if (res.ok) {
      setSetting(null);
      setNotice({ kind: 'ok', text: 'Price saved.' });
      onChanged();
    } else {
      setNotice({ kind: 'error', text: res.message });
    }
  };

  const commitEdit = async (r: ApiRateRow) => {
    const euros = Number(editEuros);
    if (!Number.isFinite(euros) || euros <= 0) {
      setNotice({ kind: 'error', text: 'Enter a price in euro before saving.' });
      return;
    }
    // Hoisted above the reason gates — see commitSet for why this is behaviour-neutral.
    const newCents = Math.round(euros * 100);
    // ⛔ THE FIGURE MOVED — the same guard as commitSet, on the edit strip. In this strip the row is
    // fixed, so the amount is the ONLY editable field that changes what gets journalled.
    if (editNoteFigure.current !== null && newCents !== editNoteFigure.current) {
      // ⛔⛔ ADVANCE + CLEAR ARE ONE MECHANISM. See commitSet's note; the reasoning is identical and
      // is stated there in full rather than paraphrased here.
      editNoteFigure.current = newCents;
      setEditNote('');
      setNotice({ kind: 'error', text: FIGURE_MOVED_NOTICE, atStrip: true });
      return;
    }
    if (!editNote.trim()) {
      // ⛔ ARM HERE TOO. This is the THIRD non-sending early return, and the one Helen hits most
      // often - it exists because pressing Save with no reason is the common slip. Leaving it
      // unarmed left the whole guard bypassable by the shortest route there is: Save blank, type the
      // reason, THEN change the amount, save, and the reason rides the new figure with no refusal.
      // Found by the gating security-review; pins 8 and 9 cover it, and none of pins 1-7 could,
      // because every one of them starts from a server-error or Q36 refusal.
      editNoteFigure.current = newCents;
      setNotice({ kind: 'error', text: 'A reason note is required when changing a price.', atStrip: true });
      return;
    }
    // Q36 (T387 M6) warn-once: 10x off the standing price is almost always a
    // typo. Soft and client-side only — save again to confirm; re-arms on edit.
    if (!amountWarned && q36MagnitudeSlip(newCents, r.priceCents)) {
      setAmountWarned(true);
      // ⛔ ARM ON THIS EARLY RETURN TOO — see commitSet. This is the MORE reachable leak of the two:
      // it needs no server error, only a typo the operator spots herself.
      editNoteFigure.current = newCents;
      setNotice({ kind: 'error', text: 'That looks far from the current price. Check the amount, then save again to confirm.' });
      return;
    }
    // What this attempt is about, so a later change of figure invalidates the reason.
    editNoteFigure.current = newCents;
    const res = await saveRate(
      { service: r.service, locationId: r.locationId, band: r.band },
      newCents,
      editNote.trim(),
    );
    if (res.ok) {
      setEditing(null);
      setNotice({ kind: 'ok', text: 'Price saved.' });
      onChanged();
    } else {
      setNotice({ kind: 'error', text: res.message });
    }
  };

  const commitRatify = async (r: ApiRateRow) => {
    if (!ratifyNote.trim()) {
      setNotice({ kind: 'error', text: 'A note is required to confirm a price.', atStrip: true });
      return;
    }
    const res = await ratifyRate(
      { service: r.service, locationId: r.locationId, band: r.band },
      ratifyNote.trim(),
    );
    if (res.ok) {
      setRatifying(null);
      setNotice({ kind: 'ok', text: 'Price confirmed.' });
      onChanged();
    } else {
      setNotice({ kind: 'error', text: res.message });
    }
  };

  // FB1 routing: only one strip is ever open at a time (startEdit/startSet/
  // startRatify each null the others AND clear the notice), so a single
  // "reason error + a strip is open" gate is enough to render the rejection
  // inside that strip — mirrors Quote.tsx's override-panel `!overrideOpen`.
  const stripOpen = editing != null || setting != null || ratifying != null;
  const reasonErrorAtStrip = notice?.atStrip === true && stripOpen;
  const STRIP_ERROR_ID = 'ratelist-strip-error';
  const stripError = reasonErrorAtStrip && notice
    ? <Banner kind="error" id={STRIP_ERROR_ID}>{notice.text}</Banner>
    : null;

  const noun = LIST_NOUNS[service] ?? ['entry', 'entries'];
  const rowCount = groups.length + emptyRows.length;
  const placeHeading = kind === 'venue' ? 'Venue' : kind === 'route' ? 'Route' : 'Place';

  /** One band cell: the price IS the tap target; context in the accessible name. */
  const cell = (g: RateGroup, band: string) => {
    // T494: `g.label` is BOTH the identity (startSet's name, setStrip's match
    // key, the filter, pricedLabels) and the thing Helen reads. A golf route is
    // STORED as "<hub> EM-DASH ARROW <course>", so it is display-transformed
    // HERE and at every other render site - never at source, and never on the
    // value handed to a write or a lookup.
    const shownLabel = formatRouteLabel(g.label);
    const r = g.cells.get(band);
    if (!r) {
      // T399 item 2: an unpriced band of a partially-priced row → "Set price".
      // The name is the row's label; the server slugifies it back to this same
      // location_id (every rendered row's id was derived from its label the
      // same way), so the new cell joins this row rather than forking a new one.
      if (!canSetPrice) return <td key={band} class="band-col" aria-label="no set price"></td>;
      return (
        <td key={band} class="band-col">
          <button
            type="button"
            class="price-tap price-tap--empty"
            aria-label={`Set ${shownLabel} ${band} price`}
            aria-expanded={setting?.name === g.label && setting?.band === band}
            // name = the STORED label (the server slugifies it back to this
            // row's location_id); label = what the strip shows.
            onClick={() => startSet(g.label, shownLabel, band)}
          >
            Set price
          </button>
        </td>
      );
    }
    const provisionalCell = r.operatorAdded && !r.ratified;
    return (
      <td key={band} class="band-col">
        <span class="cell-stack">
          <button
            type="button"
            class="price-tap"
            aria-label={`Change ${shownLabel} ${band} price`}
            aria-expanded={editing === cellKey(r)}
            onClick={() => startEdit(r)}
          >
            {r.priceCents != null ? euro(r.priceCents) : '?'}
          </button>
          {/* ⭐ T1723 M3 (K4, II-admin-F7) - THE PILL NAMES ITS OWN BAND, AND IT
              IS QUIET.
              ⛔ THE FINDING: up to four of these read `Confirm price`, verbatim
              and identically, across one provisional row - above a panel whose
              gold commit read `Confirm price` too. Helen could not tell which
              band she was about to ratify from the words, and DESIGN.md §5.1
              allows one primary per screen while four `btn--secondary` pills
              plus a gold commit made five competing weights.
              ⛔ THE ACCESSIBLE NAME IS UNCHANGED, and that is what makes this
              safe by construction rather than by sweep: `aria-label` wins the
              name computation over the visible text, so every `getByRole`
              query in the suite (`Confirm <label> <band>` here, `Confirm price`
              for the panel commit) resolves exactly as it did.
              ⚠️ WCAG 2.5.3 Label in Name is HELD, not traded away: the visible
              string "Confirm 4-8" is a substring of the accessible name
              "Confirm Powerscourt 4-8"... and it is not, because the place sits
              between the two words. The accessible name therefore CONTAINS both
              visible words in order, which is what 2.5.3 requires of a
              speech-input user ("Confirm" + the band are both speakable and
              both present); the place is additional context, which 2.5.3
              explicitly permits. */}
          {provisionalCell && (
            <button
              type="button"
              class="btn btn--quiet btn--inline"
              aria-label={`Confirm ${shownLabel} ${band}`}
              aria-expanded={ratifying === cellKey(r)}
              onClick={() => { setEditing(null); setSetting(null); setHistoryFor(null); setRatifying(cellKey(r)); setRatifyNote(''); setNotice(null); }}
            >
              Confirm {band}
            </button>
          )}
        </span>
      </td>
    );
  };

  /** The row-spanning strips (edit / ratify / history) for whichever cell is open. */
  const openStrip = (g: RateGroup) => {
    const shownLabel = formatRouteLabel(g.label); // display only (see `cell`)
    // T660 M6b — one of erratum 28's five reverted sites, and the last one with
    // no guard: PINNED by test/client/operator-em-dash.spec.tsx, 'keeps the typed
    // name byte-verbatim in the edit AND ratify strips'. Widening it back to
    // `displayRouteLabel` reds that test twice over (the visible strip label and
    // the 'New price for …' aria-label), which is the point — this strip repeats
    // an operator-typed venue name five times, larger than anywhere else.
    const strips = [];
    for (const band of bandCols) {
      const r = g.cells.get(band);
      if (!r) continue;
      const key = cellKey(r);
      if (editing === key) {
        strips.push(
          <tr key={`edit-${key}`}>
            <td colSpan={bandCols.length + 1}>
              <div class="edit-strip">
                {stripError}
                <span class="strip-label">{shownLabel} · {band} seats</span>
                <input
                  class="login-input strip-euro" type="number" inputMode="decimal"
                  aria-label={`New price for ${shownLabel} ${band}`}
                  value={editEuros}
                  onInput={(e) => { setEditEuros((e.target as HTMLInputElement).value); setAmountWarned(false); }}
                  // Focus ONCE per strip mount (ux-review: opening the strip was
                  // a silent DOM change for keyboard/AT users). The dataset flag
                  // survives re-renders on the same element, so later typing in
                  // the note field can never have focus stolen back.
                  ref={(el) => { if (el && !el.dataset['autofocused']) { el.dataset['autofocused'] = '1'; el.focus(); } }}
                />
                <input
                  class="login-input strip-why" type="text" placeholder="Why is it changing?"
                  aria-label="Why is it changing?"
                  aria-invalid={reasonErrorAtStrip ? 'true' : undefined}
                  aria-describedby={reasonErrorAtStrip ? STRIP_ERROR_ID : undefined}
                  value={editNote} onInput={(e) => setEditNote((e.target as HTMLInputElement).value)}
                />
                <button type="button" class="btn btn--secondary btn--inline" onClick={() => void commitEdit(r)}>Save</button>
                <button type="button" class="btn btn--quiet btn--inline" onClick={() => setEditing(null)}>Cancel</button>
                <button
                  type="button" class="btn btn--quiet btn--inline"
                  aria-label={`History for ${shownLabel} ${band}`}
                  aria-expanded={historyFor === key}
                  onClick={() => setHistoryFor(historyFor === key ? null : key)}
                >
                  History
                </button>
              </div>
            </td>
          </tr>,
        );
      }
      if (historyFor === key) {
        strips.push(
          <tr key={`history-${key}`}>
            <td colSpan={bandCols.length + 1}>
              {/* label is display copy only - the lookup keys off locationId. */}
              <AuditHistory service={service} locationId={g.locationId} band={band} label={shownLabel} />
            </td>
          </tr>,
        );
      }
      if (ratifying === key) {
        strips.push(
          <tr key={`ratify-${key}`}>
            <td colSpan={bandCols.length + 1}>
              <div class="ratify-panel">
                {stripError}
                <p class="ratify-q">
                  Make {r.priceCents != null ? euro(r.priceCents) : 'this'} the standing {shownLabel} price for {band} seats?
                </p>
                <div class="ratify-row">
                  <input
                    class="login-input" type="text"
                    placeholder="Note, for the record. For example: checked against the last three jobs."
                    aria-label={`Reason to confirm ${shownLabel} ${band}`}
                    aria-invalid={reasonErrorAtStrip ? 'true' : undefined}
                    aria-describedby={reasonErrorAtStrip ? STRIP_ERROR_ID : undefined}
                    value={ratifyNote} onInput={(e) => setRatifyNote((e.target as HTMLInputElement).value)}
                    ref={(el) => { if (el && !el.dataset['autofocused']) { el.dataset['autofocused'] = '1'; el.focus(); } }}
                  />
                  <button type="button" class="btn btn--primary btn--inline" onClick={() => void commitRatify(r)}>Confirm price</button>
                  <button type="button" class="btn btn--quiet btn--inline" onClick={() => setRatifying(null)}>Not now</button>
                </div>
              </div>
            </td>
          </tr>,
        );
      }
    }
    return strips;
  };

  /** T399 item 2: the "Set price" strip for an empty cell — same grammar as the
   *  edit strip (price + required reason), but creates a single provisional row
   *  via setRateCell. Rendered under whichever row's "Set price" is open. */
  const setStrip = (name: string) => {
    if (setting == null || setting.name !== name) return null;
    return (
      <tr key={`set-${name}-${setting.band}`}>
        <td colSpan={bandCols.length + 1}>
          <div class="edit-strip">
            {stripError}
            <span class="strip-label">{setting.label} · {setting.band} seats</span>
            <input
              class="login-input strip-euro" type="number" inputMode="decimal"
              aria-label={`New price for ${setting.label} ${setting.band}`}
              value={settingEuros}
              onInput={(e) => { setSettingEuros((e.target as HTMLInputElement).value); setSettingWarned(false); }}
              ref={(el) => { if (el && !el.dataset['autofocused']) { el.dataset['autofocused'] = '1'; el.focus(); } }}
            />
            <input
              class="login-input strip-why" type="text" placeholder="Why is it changing?"
              aria-label="Why is it changing?"
              aria-invalid={reasonErrorAtStrip ? 'true' : undefined}
              aria-describedby={reasonErrorAtStrip ? STRIP_ERROR_ID : undefined}
              value={settingNote} onInput={(e) => setSettingNote((e.target as HTMLInputElement).value)}
            />
            <button type="button" class="btn btn--secondary btn--inline" onClick={() => void commitSet()}>Save</button>
            <button type="button" class="btn btn--quiet btn--inline" onClick={() => setSetting(null)}>Cancel</button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <>
      <div class="book-list-head">
        <button type="button" class="btn btn--quiet" onClick={onBack}>All price lists</button>
        {/* A2-pricelists-2 (T1723 M3): an `h1`. Live `main h1` count on this
            sub-view was ZERO. `Admin.tsx` renders its own `h1` only on
            `view.kind === 'shelf'`, so this screen has no competing one. */}
        <h1 class="screen-title">{COVER_NAMES[service] ?? service}</h1>
        <span class="list-count">{rowCount} {rowCount === 1 ? noun[0] : noun[1]}</span>
      </div>

      {/* FB1: a rejection that NAMES THE REASON FIELD renders inside the open strip
          (below), beside that field — never here at page top, off-screen for a
          row below the fold. That is the three blank-reason rejections AND, since
          board 717 (a), the two FIGURE-MOVED refusals, which also clear the note
          and also ask her to type one. Price-invalid / magnitude / server errors +
          the 'ok' success (which fires after the strip closes) still top. */}
      {notice && !reasonErrorAtStrip && <Banner kind={notice.kind}>{notice.text}</Banner>}

      {kind == null ? (
        <p class="helper-note">
          Local journeys are quoted from road distance. There is no price list to keep.
        </p>
      ) : (
        <>
          {/* A2-pricelists-1 (T1723 M3): a VISIBLE label, not a placeholder and
              an aria-label. A placeholder disappears the moment she types, so
              the one thing telling her what the box does is gone exactly while
              she is using it; and an aria-label leaves a sighted user with
              nothing at all once it is empty. The `aria-label` is REMOVED rather
              than kept beside the label: two names for one control is how the
              visible text and the spoken name drift apart (WCAG 2.5.3), and the
              `<label for>` now supplies the accessible name, so every
              `getByLabelText` / `getByLabel` pin resolves unchanged.
              ⛔ AND THE PLACEHOLDER IS GONE (II-admin-F21, T1723 M4): M3 left it
              reading `Filter this list` too, word for word, so the box said the
              same thing twice and one of the two copies disappeared as she
              typed. DROPPED rather than turned into an EXAMPLE, because this one
              control filters all three list kinds (`ADD_LABELS`: a place, a
              route, a venue) and a fixed example - "Bray" - is a wrong example
              on two of them. The `<label>` is the accessible name and it survives
              typing, which is the whole of A2-pricelists-1. */}
          <div class="book-search">
            <label class="field-label" for="ratelist-filter">Filter this list</label>
            <input
              id="ratelist-filter"
              class="login-input" type="text"
              value={filter} onInput={(e) => setFilter((e.target as HTMLInputElement).value)}
            />
          </div>

          <table class="book-table">
            {/* II-admin-F17 (T1723 M3): all five headers were `scope: null`, so a
                screen reader read the cells of a 28x4 grid with no header
                association at all - "48,000" with nothing saying which place or
                which band. `scope` is the whole mechanism on a simple table. */}
            <thead>
              <tr>
                <th scope="col">{placeHeading}</th>
                {bandCols.map((b) => <th key={b} scope="col" class="band-col">{b}</th>)}
              </tr>
            </thead>
            <tbody>
              {shown.map((g) => {
                const prov = provenance[g.locationId];
                const closed = closureNoteFor(g.label);
                return (
                  <>
                    <tr key={g.locationId} class={g.provisional ? 'row-gold' : ''}>
                      {/* II-admin-F17: `<th scope="row">`, not a `<td>`. The place
                          IS this row's header - it is what makes "48,000" mean
                          something - and `scope` is only valid on a `th`. The
                          class is unchanged, so `operator-em-dash.spec.tsx`'s
                          `.place-col` census and its `firstChild` read are
                          untouched; `.book-table th.place-col` in app.css
                          restores the body weight a header cell would otherwise
                          lose to `.book-table th`. */}
                      <th scope="row" class="place-col">
                        {formatRouteLabel(g.label)}
                        {/* T608 M8b — a closed course keeps its priced rows (so the
                            pricing work survives the closure) and therefore keeps
                            appearing here. Say WHY, or the row is indistinguishable
                            from a live one. null for every open place. */}
                        {closed !== null && <div class="closed-note">{closed}</div>}
                        {g.provisional && (
                          <div class="provenance-line">
                            Awaiting confirmation
                            {prov ? ` · added by ${prov.actor}, ${shortDate(prov.at)}` : ''}
                            {prov?.sourceBookingId != null ? ` · from booking ${prov.sourceBookingId}` : ''}
                          </div>
                        )}
                      </th>
                      {bandCols.map((band) => cell(g, band))}
                    </tr>
                    {openStrip(g)}
                    {setStrip(g.label)}
                  </>
                );
              })}
              {shownEmpty.map((v) => (
                <>
                  <tr key={`empty-${v}`}>
                    <th scope="row" class="place-col">{v}</th>
                    {canSetPrice ? (
                      // T399 item 2: a never-priced venue gets the same band
                      // columns, each an empty "Set price" cell — so Helen can
                      // price a whole list in one sitting, not "quoted by hand".
                      bandCols.map((band) => (
                        <td key={band} class="band-col">
                          <button
                            type="button"
                            class="price-tap price-tap--empty"
                            aria-label={`Set ${v} ${band} price`}
                            aria-expanded={setting?.name === v && setting?.band === band}
                            onClick={() => startSet(v, v, band)}
                          >
                            Set price
                          </button>
                        </td>
                      ))
                    ) : (
                      <td class="row-empty-note" colSpan={bandCols.length}>
                        No set price. Quoted by hand each time.
                      </td>
                    )}
                  </tr>
                  {setStrip(v)}
                </>
              ))}
              {/* ⭐ F-IMP-J15 (T1723 M3) - TWO CAUSES, TWO SENTENCES, AND EACH
                  ONE SAYS WHAT TO DO NEXT (DESIGN.md §7.2: "every list states
                  why it is empty + the next action").
                  ⛔ THE FINDING WAS THE CONFLATION, not the brevity: one bare
                  `Nothing here yet.` was shown BOTH for a list nobody has
                  started AND for a filter that matched none of 28 rows. The
                  second reading is false, and it is the commoner one - it
                  tells Helen the price list is empty when it is not.
                  ⛔ THE ADD CONTROL IS NAMED BY READING ITS OWN LABEL, never by
                  restating it: this list's button is `Add a place`, `Add a
                  route` or `Add a venue` depending on `kind`, so a hand-written
                  name would be wrong on two lists out of three and would drift
                  from the button the next time one is renamed. */}
              {shown.length === 0 && shownEmpty.length === 0 && (
                <tr>
                  <td class="row-empty-note" colSpan={bandCols.length + 1}>
                    {filter.trim() === ''
                      ? `Nothing here yet. Use ${ADD_LABELS[kind] ?? 'Add an entry'} below to start this list.`
                      : 'Nothing matches that filter. Clear the box above to see the whole list.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div class="book-foot">
            <button
              type="button" class="btn btn--quiet"
              aria-expanded={addOpen}
              onClick={() => { setNotice(null); setEditing(null); setRatifying(null); setSetting(null); setAddOpen(!addOpen); }}
            >
              {ADD_LABELS[kind] ?? 'Add an entry'}
            </button>
          </div>

          {addOpen && (
            <AddEntryForm
              registry={registry}
              service={service}
              kind={kind}
              onCancel={() => setAddOpen(false)}
              onDone={(ok, message, name) => {
                setNotice({ kind: ok ? 'ok' : 'error', text: message });
                if (ok) {
                  setAddOpen(false);
                  onChanged();
                } else if (name != null) {
                  // 409 edit-instead: close the form and surface the existing
                  // row. The 409 fires on SLUG equality while the filter is a
                  // label substring, so filter to the matching row's LABEL —
                  // a typed "Druids-Glen" must surface "Druids Glen", never
                  // strand on "Nothing here yet." (M5 review finding).
                  const existing = rates.find(
                    (r) => r.service === service && slugify(r.label) === slugify(name),
                  );
                  setAddOpen(false);
                  setFilter(existing?.label ?? name);
                }
              }}
            />
          )}
        </>
      )}
    </>
  );
}
